import React, { useMemo } from 'react'
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Avatar,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useQuery } from 'react-query'
import getStagesWithStandings from '../../../Apis/stages/getStagesWithStandings'
import QueryKeys from '../../../utils/constants/queryKeys'
import LoaderDots from '../../../components/loaders/LoaderDots/LoaderDots'
import ContainerPadding from '../../../components/containerPadding/containerPadding'
import useUrlSearchParams from '../../../hooks/useUrlSearchParams'
import useGetLocalStorage from '../../../hooks/useGetLocalStorage'
import localStorageIds from '../../../utils/constants/localStorageIds'
import COLORS from '../../../utils/constants/colors'
import GROUP_TYPES from '../../../utils/constants/groupTypes'
import Bracket from './Bracket/Bracket'
import standingsStyles from './Standings.styles'

const useStyles = makeStyles(standingsStyles)

const Standings = () => {
  const classes = useStyles()
  const { org: orgId } = useUrlSearchParams(['org']) || {}
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_SEASON]: selectedSeason } = localStorageValue || {}
  const seasonId = selectedSeason?.id
  const stageIdFromStorage = localStorageValue?.[localStorageIds.SELECTED_STAGE]?.id || null
  const groupIdFromStorage = localStorageValue?.[localStorageIds.SELECTED_GROUP]?.id || null

  const {
    data: stages,
    isLoading,
    error,
  } = useQuery(
    [QueryKeys.GET_STAGES_WITH_STANDINGS, orgId, seasonId],
    () => getStagesWithStandings({ orgId, seasonId }),
    {
      enabled: Boolean(orgId && seasonId),
    },
  )

  // Derive selected stage and group from localStorage and stages data
  const selectedStage = useMemo(() => {
    if (!stageIdFromStorage || !stages) return null
    return stages.find((s) => s.id === stageIdFromStorage)
  }, [stageIdFromStorage, stages])

  const selectedGroup = useMemo(() => {
    if (!groupIdFromStorage || !selectedStage) return null
    return selectedStage.groups?.find((g) => g.id === groupIdFromStorage)
  }, [groupIdFromStorage, selectedStage])

  const getDisplayTitle = () => {
    if (selectedGroup && selectedStage) {
      return `${selectedSeason?.name} - ${selectedStage.name} - ${selectedGroup.name} - Standings`
    }
    if (selectedStage) {
      return `${selectedSeason?.name} - ${selectedStage.name} - Standings`
    }
    return `${selectedSeason?.name} - Standings`
  }

  if (isLoading) {
    return (
      <ContainerPadding paddingValue='45%'>
        <LoaderDots
          style={{
            fill: COLORS.orange,
            width: 70,
            height: 70,
          }}
        />
      </ContainerPadding>
    )
  }

  if (error) {
    return (
      <div className={classes.container}>
        <Typography color='error'>Error loading standings: {error.message}</Typography>
      </div>
    )
  }

  // Check if both stage and group are selected
  if (!selectedStage || !selectedGroup) {
    return (
      <div className={classes.container}>
        <Paper style={{ padding: '40px', textAlign: 'center' }}>
          <Typography variant='h6' color='textSecondary'>
            Please select both a Stage and a Group to view standings
          </Typography>
        </Paper>
      </div>
    )
  }

  // Bracket groups have no meaningful standings — show the knockout bracket instead.
  if (selectedGroup.type === GROUP_TYPES.BRACKET) {
    return (
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography variant='h5'>
            {`${selectedSeason?.name} - ${selectedStage.name} - ${selectedGroup.name}`}
          </Typography>
        </div>
        <Bracket orgId={orgId} group={selectedGroup} />
      </div>
    )
  }

  // Get standings for the selected group, enriched with team info
  const teams = selectedGroup.teams || []
  const standings = (selectedGroup.standings || []).map((standing) => {
    const team = teams.find((t) => t.seasonTeamId === standing.seasonTeamId)
    return {
      ...standing,
      teamName: team?.teamName || 'Unknown',
      teamLogoUrl: team?.teamLogoUrl || null,
      color: team?.color || null,
    }
  })

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant='h5'>{getDisplayTitle()}</Typography>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        {standings.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ paddingTop: 10, paddingBottom: 10 }}>Pos</TableCell>
                <TableCell style={{ paddingTop: 10, paddingBottom: 10 }}>Team</TableCell>
                <TableCell align='center' style={{ paddingTop: 10, paddingBottom: 10 }}>
                  P
                </TableCell>
                <TableCell align='center' style={{ paddingTop: 10, paddingBottom: 10 }}>
                  W
                </TableCell>
                <TableCell align='center' style={{ paddingTop: 10, paddingBottom: 10 }}>
                  L
                </TableCell>
                <TableCell align='center' style={{ paddingTop: 10, paddingBottom: 10 }}>
                  PF
                </TableCell>
                <TableCell align='center' style={{ paddingTop: 10, paddingBottom: 10 }}>
                  PA
                </TableCell>
                <TableCell align='center' style={{ paddingTop: 10, paddingBottom: 10 }}>
                  +/-
                </TableCell>
                <TableCell align='center' style={{ paddingTop: 10, paddingBottom: 10 }}>
                  Pts
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {standings.map((standing, index) => (
                <TableRow key={standing.seasonTeamId || index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <div className={classes.teamCell}>
                      <Avatar
                        src={standing.teamLogoUrl}
                        alt={standing.teamName}
                        className={classes.teamLogo}
                      >
                        {!standing.teamLogoUrl && standing.teamName?.charAt(0)}
                      </Avatar>
                      {standing.teamName}
                    </div>
                  </TableCell>
                  <TableCell align='center'>{standing.gamesPlayed || 0}</TableCell>
                  <TableCell align='center'>{standing.wins || 0}</TableCell>
                  <TableCell align='center'>{standing.losses || 0}</TableCell>
                  <TableCell align='center'>{standing.pointsFor || 0}</TableCell>
                  <TableCell align='center'>{standing.pointsAgainst || 0}</TableCell>
                  <TableCell align='center'>{standing.pointDifferential || 0}</TableCell>
                  <TableCell align='center'>
                    <strong>{standing.points || 0}</strong>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box style={{ padding: '40px', textAlign: 'center' }}>
            <Typography color='textSecondary'>
              No standings data available for this group
            </Typography>
          </Box>
        )}
      </TableContainer>
    </div>
  )
}

export default Standings
